<script lang="ts">
	import { Genres } from '$lib/Globals';
	import type { FeedItemStatus } from '$lib/types/FeedStatusTypes';
	import { DatoImage } from '@atoms';
	import EventLabels from '@comp/EventLabels.svelte';
	import { Breakpoints, DateFormats } from '@tools';

	let {
		data
	}: {
		data: {
			id: string;
			_modelApiKey: string;
			eventId: string;
			path: string;
			title: string;
			subtitle: string;
			location: string;
			genres: string[];
			ticketUrl: string;
			showings: any[];
			status: FeedItemStatus;
			badge?: string;
			content: any[];
			poster: any;
			minimumPrice: number;
			maximumPrice: number;
		};
	} = $props();
</script>

<div class="container">
	<EventLabels status={data.status} badge={data.badge} />
	{#if data.poster}
		<a class="image-container" href={data.path}>
			<DatoImage responsiveImage={data.poster} />
		</a>
	{/if}
	<div class="info">
		<a class="title text:h4" href={data.path}>{data.title}</a>
		{#if data.subtitle}
			<p class="subtitle">{data.subtitle}</p>
		{/if}
		{#if data.showings.length > 0}
			<div class="date">
				{DateFormats.dateWithWeekday(data.showings[0].date)}
				{#if data.showings.length > 1}
					+ flere
				{/if}
			</div>
		{/if}
		<div class="data">
			<b class="text:body-alt bold">Pris</b>
			<p class="text:body">
				{data.maximumPrice <= 0
					? 'Gratis'
					: data.minimumPrice === data.maximumPrice
						? `${data.maximumPrice} kr.`
						: `${data.minimumPrice}-${data.maximumPrice} kr.`}
			</p>
			<b class="text:body-alt bold">Genre</b>
			<p class="text:body">
				{#each data.genres as id}
					{@const genre = Genres.getById(id)}
					{#if genre}
						<a class="genre" href={`/kalender?genres=${genre.slug}`}>{genre.hash}</a>
					{/if}
				{/each}
			</p>
			{#if data.location}
				<b class="text:body-alt bold">Sal</b>
				<p class="text:body">{data.location}</p>
			{/if}
		</div>
	</div>
	<div class="buttons">
		{#if Breakpoints.md && data.ticketUrl}
			<a class="button:pill" href={data.ticketUrl}
				>{data.maximumPrice <= 0 ? 'Hent billet' : 'Køb billet'}</a
			>
			<a class="button:outline" href={data.path}>Læs mere</a>
		{:else}
			<a class="button:pill" href={data.path}>Læs mere</a>
		{/if}
	</div>
</div>

<style lang="scss">
	.container {
		grid-column-end: span 2;
		background-color: $colorCardBackground;
		color: $colorCardForeground;
		display: flex;
		align-items: stretch;
		flex-direction: column;
		position: relative;

		@include sm {
			flex-direction: row;
		}

		:global(.EventLabels) {
			position: absolute;
			top: 0;
			left: 0;
			z-index: 4;
			pointer-events: none;
		}
	}

	.image-container {
		position: relative;
		z-index: 1;
		overflow: hidden;
		aspect-ratio: 1 / 0.85;

		@include sm {
			flex: 0 0 250px;
			aspect-ratio: 1 / 1;
		}

		:global(.asset) {
			transition: transform $transition;
		}

		&:hover,
		&:global(:has(+ .info .title:hover)) {
			:global(.asset) {
				transform: scale(1.1);
			}
		}
	}

	.info {
		flex: 1 1 auto;
		padding: $gap5;
		display: flex;
		flex-direction: column;

		@include sm {
			padding: $gap5 $gap6;
		}
	}

	.title {
		text-decoration: none;
	}

	.data {
		display: grid;
		grid-template-columns: minmax(60px, max-content) 1fr;
		grid-auto-rows: max-content;
		width: 100%;
		margin-top: auto;
		padding-top: $gap4;

		p {
			display: flex;
			flex-wrap: wrap;
			gap: 0.25em 0.5em;
		}
	}

	.subtitle {
		padding-top: $gap1;
	}

	.date {
		padding-top: $gap3;
	}

	.buttons {
		flex: 0 0 auto;
		display: flex;
		justify-content: end;
		gap: $gap3;
		padding: $gap5;
		padding-top: 0;

		@include sm {
			padding: $gap5 $gap6;
			flex-direction: column;
			padding-left: 0;
		}
	}

	.button\:pill {
		width: 100%;

		@include sm {
			width: auto;
		}
	}

	.genre {
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}
</style>
